import { debounce } from "lodash"
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { MenuItem, Typography } from "@mui/material"

import Selector from "@/components/form/selector/Selector"
import TextField from "@/components/form/textField/TextField"
import Icon from "@/components/icon/Icon.tsx"
import { colors } from "@/components/colors.ts"

import { ReportTemplate, ReportTemplateMetadata } from "@/types/report.ts"
import "./settingTemplateContainer.scss"
import moment from "moment-timezone"
import { useUpdateReportTemplateLastUsedMutation } from "@/hooks/api/useReportQuery"

export default function SettingTemplateContainer({
  idfCurrentTemplate,
  template,
  setTemplate,
  templates,
  onChangeTemplate,
  reportType,
}: {
  idfCurrentTemplate: number | null
  template?: ReportTemplate | null
  setTemplate: Dispatch<SetStateAction<ReportTemplate | null>>
  templates: ReportTemplateMetadata[]
  onChangeTemplate: (idfTemplate: number | null) => void
  reportType: "daily" | "trend"
}) {
  const { t } = useTranslation()
  const { mutate: updateLastUsed } = useUpdateReportTemplateLastUsedMutation()

  const [open, setOpen] = useState<boolean>(false)
  const [templateName, setTemplateName] = useState(template?.name || "")
  const [templateLastUpdated, setTemplateLastUpdated] = useState(
    moment(template?.dateUpdated).format("yyyy-MM-DD HH:mm:ss"),
  )

  useEffect(() => {
    if (!template) {
      return
    }

    setTemplateName(template.name)
    setTemplateLastUpdated(moment(template.dateUpdated).format("yyyy-MM-DD HH:mm:ss"))
  }, [template])

  const debouncedUpdate = useMemo(
    () =>
      debounce((newName: string) => {
        setTemplate({
          ...template,
          name: newName,
        })
      }, 200),
    [template, setTemplate],
  )

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    if (newValue.length <= 35) {
      setTemplateName(newValue)
      debouncedUpdate(newValue)
    }
  }

  const getTemplateNameError = () => {
    if (templateName.length === 35) {
      return t("rules.max_len", { name: t("templateName"), max_len: 35 })
    }
    return ""
  }

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel()
    }
  }, [debouncedUpdate])

  return (
    <div className="SettingTemplateContainer">
      <div className="template-card">
        <div className="template-content">
          <Typography variant="body2" className="template-label">
            {t("templateName")}
          </Typography>
          <div className="template-text">
            <TextField
              value={templateName}
              onChange={handleNameChange}
              size="small"
              className="template-name-input"
              error={templateName.length === 35}
              message={getTemplateNameError()}
              showMessage={templateName.length === 35}
              inputProps={{ maxLength: 35 }}
            />

            <Typography variant="body2" className="last-updated-text">
              Last updated {templateLastUpdated}
            </Typography>
          </div>
        </div>
      </div>
      <div className="template-selector-wrapper">
        <Selector
          type="select"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          className="template-selector"
          value={idfCurrentTemplate}
          onChange={(e) => {
            const newTemplateId = e.target.value as number
            onChangeTemplate(newTemplateId)
            if (newTemplateId) {
              updateLastUsed({ idfReportTemplate: newTemplateId, reportType })
            }
          }}
          renderValue={(selectedIdfTemplate) => {
            const selectedTemplate = templates?.find((t) => t.idfTemplate === selectedIdfTemplate)

            return (
              <div className="selected-value">
                <img src="/images/ri-table-line.png" alt="" className="template-icon" />
                <span className="selected-text">{selectedTemplate?.name}</span>
              </div>
            )
          }}>
          {templates?.map((template) => (
            <MenuItem
              key={template.idfTemplate}
              value={template.idfTemplate}
              className="setting-template-menu">
              <div className="item-left">
                <span className="item-text">{template.name}</span>
              </div>
              {template.idfTemplate === idfCurrentTemplate && (
                <div className="item-right">
                  <Icon type="check" size={16} color={colors.neutral.grey[1000]} />
                </div>
              )}
            </MenuItem>
          ))}
        </Selector>
      </div>
    </div>
  )
}
